import React, { useRef } from 'react';
import { Box, Container, Typography, Chip, useTheme } from '@mui/material';
import { motion, useScroll, useTransform } from 'motion/react';
import { content } from '../../data/content';
import { useAnimationVariants } from '../../hooks/useAnimationVariants';
import { useTiltEffect } from '../../hooks/useTiltEffect';

const MotionBox = motion.create(Box);

interface ExperienceCardProps {
  item: typeof content.experience.items[0];
  index: number;
  total: number;
}

const ExperienceCard: React.FC<ExperienceCardProps> = ({ item, index, total }) => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const tilt   = useTiltEffect({ maxRotation: 4 });

  const yearShort = item.period.match(/\d{4}/)?.[0]?.slice(2) ?? '';

  return (
    <MotionBox
      initial={{ opacity: 0, x: -40 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.7, delay: index * 0.14, ease: [0.16, 1, 0.3, 1] }}
      sx={{ position: 'relative', mb: index < total - 1 ? { xs: 5, md: 7 } : 0 }}
    >
      <Box
        ref={tilt.ref}
        onMouseMove={tilt.handleMouseMove}
        onMouseLeave={tilt.handleMouseLeave}
        component={motion.div}
        style={{ rotateX: tilt.rotateX, rotateY: tilt.rotateY, transformStyle: 'preserve-3d' } as any}
        sx={{ display: 'flex', gap: { xs: 0, md: 4 }, perspective: '1000px' }}
      >
        {/* Left: Year column (desktop only) */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'center', width: 72, flexShrink: 0, pt: 0.5 }}>
          <Typography
            sx={{
              fontFamily: '"Space Grotesk", sans-serif', fontSize: '2.5rem', fontWeight: 800,
              color: theme.palette.primary.main, opacity: 0.55, lineHeight: 1,
              writingMode: 'vertical-rl', textOrientation: 'mixed',
              transform: 'rotate(180deg)', letterSpacing: '-0.04em',
              userSelect: 'none',
            }}
          >
            '{yearShort}
          </Typography>
          {/* Company initial badge */}
          <Box sx={{ mt: 2, width: 36, height: 36, borderRadius: '50%', background: item.current ? `linear-gradient(135deg, ${theme.palette.primary.main}, #a78bfa)` : `linear-gradient(135deg, ${theme.palette.divider}, ${theme.palette.text.secondary}33)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 800, fontSize: '0.875rem', color: item.current ? '#030712' : 'text.secondary' }}>
              {item.company[0]}
            </Typography>
          </Box>
        </Box>

        {/* Right: Card content — open layout */}
        <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          {/* Top accent line */}
          <Box sx={{ height: 3, borderRadius: '2px', mb: 3, background: item.current ? `linear-gradient(90deg, ${theme.palette.primary.main}, #a78bfa)` : `linear-gradient(90deg, ${theme.palette.divider}, transparent)` }} />

          {/* Spotlight from mouse */}
          <Box
            component={motion.div}
            style={{ background: `radial-gradient(circle at ${tilt.glowX}% ${tilt.glowY}%, rgba(129,140,248,0.06) 0%, transparent 60%)` } as any}
            sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}
          />

          {/* Card with current-role wash */}
          <Box
            sx={{
              p: { xs: 3, md: 4 }, borderRadius: '16px', position: 'relative',
              border: `1px solid ${item.current ? `${theme.palette.primary.main}30` : theme.palette.divider}`,
              background: item.current
                ? (isDark ? 'linear-gradient(135deg, rgba(129,140,248,0.06) 0%, transparent 60%)' : 'linear-gradient(135deg, rgba(99,102,241,0.04) 0%, transparent 60%)')
                : 'transparent',
              backdropFilter: 'blur(4px)',
              transition: 'border-color 280ms ease, box-shadow 280ms ease',
              '&:hover': {
                borderColor: `${theme.palette.primary.main}44`,
                boxShadow: `0 0 32px ${theme.palette.primary.main}14, 0 8px 24px rgba(0,0,0,0.15)`,
              },
            }}
          >
            {/* Large watermark number */}
            <Typography sx={{ position: 'absolute', right: 16, top: 8, fontFamily: '"Space Grotesk", sans-serif', fontSize: '4rem', fontWeight: 800, color: theme.palette.primary.main, opacity: isDark ? 0.05 : 0.04, lineHeight: 1, userSelect: 'none', pointerEvents: 'none' }}>
              {String(index + 1).padStart(2, '0')}
            </Typography>

            {/* Role + Current badge */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', fontSize: { xs: '1.25rem', md: '1.5rem' }, lineHeight: 1.2 }}>
                {item.role}
              </Typography>
              {item.current && (
                <Chip
                  size="small"
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#34d399', boxShadow: '0 0 6px rgba(52,211,153,0.8)', animation: 'pulse-ring 2s ease-out infinite' }} />
                      Current
                    </Box>
                  }
                  sx={{ height: 24, fontSize: '0.6875rem', fontFamily: '"JetBrains Mono", monospace', bgcolor: `${theme.palette.primary.main}18`, color: 'primary.main', border: `1px solid ${theme.palette.primary.main}44`, borderRadius: '5px' }}
                />
              )}
            </Box>

            {/* Company + period row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Typography variant="body1" sx={{ color: item.current ? 'primary.main' : 'text.secondary', fontWeight: 600, background: item.current ? `linear-gradient(135deg, ${theme.palette.primary.main}, #a78bfa)` : 'none', WebkitBackgroundClip: item.current ? 'text' : 'unset', WebkitTextFillColor: item.current ? 'transparent' : 'unset', backgroundClip: item.current ? 'text' : 'unset' }}>
                {item.company}
              </Typography>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1.25, py: 0.5, borderRadius: '6px', bgcolor: `${theme.palette.primary.main}0D`, border: `1px solid ${theme.palette.primary.main}20` }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', whiteSpace: 'nowrap' }}>
                  {item.period}
                </Typography>
              </Box>
            </Box>

            {/* Bullet points */}
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
              {item.desc.map((point, i) => (
                <Box key={i} component="li" sx={{ display: 'flex', gap: 1.5, mb: i < item.desc.length - 1 ? 1.5 : 0, alignItems: 'flex-start' }}>
                  <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', background: `linear-gradient(135deg, ${theme.palette.primary.main}, #a78bfa)`, mt: '9px', flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                    {point}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </MotionBox>
  );
};

const Experience: React.FC = () => {
  const theme     = useTheme();
  const { overline, title, items } = content.experience;
  const { fadeUp, stagger } = useAnimationVariants();

  const sectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start 75%', 'end 25%'] });
  const timelineScaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <Box id="experience" ref={sectionRef} sx={{ py: { xs: 10, md: 16 }, bgcolor: 'background.default', position: 'relative', overflow: 'hidden' }}>
      {/* Orb */}
      <Box sx={{ position: 'absolute', top: '10%', right: '-10%', width: '35%', paddingBottom: '35%', borderRadius: '50%', background: `radial-gradient(circle, ${theme.palette.primary.main}0A 0%, transparent 70%)`, pointerEvents: 'none', filter: 'blur(60px)', zIndex: 0 }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <MotionBox variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }} sx={{ mb: { xs: 7, md: 10 } }}>
          <MotionBox variants={fadeUp}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', letterSpacing: '0.14em', mb: 1.5, display: 'block' }}>
              {overline}
            </Typography>
          </MotionBox>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <MotionBox variants={fadeUp}>
              <Typography variant="h2" component="h2" sx={{ color: 'text.primary', fontWeight: 700 }}>
                {title}
              </Typography>
            </MotionBox>
            <MotionBox variants={fadeUp}>
              <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontSize: { xs: '3rem', md: '5rem' }, fontWeight: 800, color: theme.palette.primary.main, opacity: 0.05, lineHeight: 1, userSelect: 'none', display: { xs: 'none', md: 'block' } }}>
                {String(items.length).padStart(2, '0')}
              </Typography>
            </MotionBox>
          </Box>
        </MotionBox>

        {/* Timeline layout */}
        <Box sx={{ position: 'relative', pl: { xs: 0, md: 0 } }}>
          {/* Scroll-driven vertical track */}
          <Box sx={{ position: 'absolute', left: { xs: 0, md: 72 + 16 }, top: 0, bottom: 0, width: 3, overflow: 'hidden', display: { xs: 'none', md: 'block' } }}>
            <Box
              component={motion.div}
              style={{ scaleY: timelineScaleY, originY: 0 }}
              sx={{ width: '100%', height: '100%', background: `linear-gradient(to bottom, ${theme.palette.primary.main}, #a78bfa, #34d399)`, borderRadius: '2px' }}
            />
          </Box>

          {/* Experience items */}
          <Box sx={{ pl: { xs: 0, md: `${72 + 16 + 24}px` } }}>
            {items.map((item, i) => (
              <ExperienceCard key={item.company} item={item} index={i} total={items.length} />
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Experience;
